import { GoogleMark } from "@/components/GoogleMark";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Eye, EyeOff, ArrowRight } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { apiError, pendingSignup } from "./homeData";

const EMPTY = { company_name: "", depot_name: "", name: "", email: "", phone: "", password: "" };
const F = ({ label, id, children, hint }) => (
  <div className="space-y-1.5">
    <Label htmlFor={id}>{label}{hint && <span className="ml-1 font-normal text-muted-foreground">{hint}</span>}</Label>{children}
  </div>
);

export const CompanySignupDialog = ({ open, onOpenChange }) => {
  const { registerCompany } = useAuth();
  const navigate = useNavigate();
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));

  const google = () => {
    if (!f.company_name.trim()) { toast.error("Add your company name first"); return; }
    pendingSignup.set({ company_name: f.company_name.trim(), depot_name: f.depot_name.trim(), phone: f.phone.trim() });
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await registerCompany(f);
      toast.success(`Welcome to Cross Jobs, ${f.name.trim().split(" ")[0]}`);
      onOpenChange(false);
      setF(EMPTY);
      navigate("/");
    } catch (err) {
      toast.error(apiError(err, "Could not create your company — please try again."));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !busy && onOpenChange(o)}>
      <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto p-0 gap-0" data-testid="company-signup-dialog">
        <div className="h-2 home-hazard rounded-t-lg" />
        <div className="p-6 sm:p-8">
          <DialogHeader className="text-left">
            <DialogTitle className="font-display text-2xl font-extrabold tracking-tight">Start your company on Cross Jobs</DialogTitle>
            <DialogDescription>For company owners and supervisors. You&apos;ll invite your workers from the dashboard once you&apos;re in.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="mt-6 space-y-4" data-testid="company-signup-form">
            <div className="grid sm:grid-cols-2 gap-4">
              <F label="Company name" id="su-company"><Input id="su-company" required maxLength={60} value={f.company_name} onChange={set("company_name")} placeholder="Southern Cross Plastering" data-testid="signup-company-name" /></F>
              <F label="First depot" id="su-depot" hint="(optional)"><Input id="su-depot" maxLength={60} value={f.depot_name} onChange={set("depot_name")} placeholder="e.g. Head office" data-testid="signup-depot-name" /></F>
            </div>
            <button type="button" onClick={google} disabled={busy} data-testid="signup-google-btn"
              className="w-full inline-flex items-center justify-center gap-3 rounded-full border border-border bg-white h-12 font-semibold text-[#0F172A] hover:bg-secondary disabled:opacity-60 transition-colors">
              <GoogleMark /> Continue with Google
            </button>
            <p className="-mt-2 text-xs text-muted-foreground text-center">Uses your Google name and email, so there&apos;s no password to remember.</p>
            <div className="flex items-center gap-3 text-xs text-muted-foreground"><span className="h-px flex-1 bg-border" />or sign up with email<span className="h-px flex-1 bg-border" /></div>
            <F label="Your full name" id="su-name"><Input id="su-name" required maxLength={80} value={f.name} onChange={set("name")} placeholder="Jess Murray" data-testid="signup-name" /></F>
            <div className="grid sm:grid-cols-2 gap-4">
              <F label="Work email" id="su-email"><Input id="su-email" type="email" required value={f.email} onChange={set("email")} placeholder="you@company.com.au" data-testid="signup-email" /></F>
              <F label="Phone" id="su-phone" hint="(optional)"><Input id="su-phone" type="tel" maxLength={40} value={f.phone} onChange={set("phone")} placeholder="04xx xxx xxx" data-testid="signup-phone" /></F>
            </div>
            <F label="Password" id="su-pw">
              <div className="relative">
                <Input id="su-pw" type={showPw ? "text" : "password"} required minLength={6} maxLength={128} value={f.password} onChange={set("password")} placeholder="At least 6 characters" className="pr-11" data-testid="signup-password" />
                <button type="button" onClick={() => setShowPw((v) => !v)} aria-label={showPw ? "Hide password" : "Show password"} data-testid="signup-toggle-password"
                  className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 inline-flex items-center justify-center rounded-md text-muted-foreground hover:text-foreground hover:bg-secondary">
                  {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </F>
            <button type="submit" disabled={busy} data-testid="signup-submit-btn"
              className="w-full mt-2 inline-flex items-center justify-center gap-2 rounded-full bg-[#EA580C] h-12 font-bold text-white hover:bg-[#C2410C] disabled:opacity-60 transition-colors">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Create company &amp; open dashboard <ArrowRight className="w-4 h-4" /></>}
            </button>
            <p className="text-xs text-muted-foreground text-center">Workers don&apos;t sign up here — they get an invite from you.</p>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
};
